<template>
  <div class="w-2/3">
    <div class="md:flex md:items-center md:justify-between md:space-x-5 mb-2">
      <div class="flex items-center space-x-3 mb-1">
        <div class="flex-shrink-0">
          <div class="relative bg-violet-600/70 rounded-md p-1">
            <mdicon name="code-block-tags" size="30" class="text-violet-100" aria-hidden="true"></mdicon>
          </div>
        </div>
        <div class="">
          <h1 class="mb-1 text-xl font-semibold text-gray-800">Customize Appearance and Embed Bot on your website</h1>
        </div>
      </div>
    </div>

    <TabsResponsive v-model:tabs="tabs" v-model:show-all="showAllTabsContent"></TabsResponsive>

    <h2 v-if="showAllTabsContent" class="text-xl font-semibold leading-7 text-gray-600 border-b border-gray-300 py-2">Appearance</h2>
    <div :class="[tabs[0].active ? '' : 'hidden']">
      <div class="w-full rounded-lg p-2 my-2 border bg-slate-50">
        <div class="flex items-start justify-between">
          <div>
            <div class="mb-1 text-md font-semibold text-gray-800">Bot Test Page</div>
            <div class="mb-2 text-xs text-gray-800">Create external test page for the bot with current Appearance configuration</div>
          </div>
          <Button label="Publish Bot Test Page" size="small" icon="pi pi-arrow-up-right" severity="primary" @click="PublishTestPage" />
        </div>
        <div v-if="botsStore.botTestUrl && botsStore.botTestUrl !== ''">
          <a :href="botsStore.botTestUrl" target="_blank" class="text-sm font-semibold text-blue-500 cursor-pointer hover:underline">{{ botsStore.botTestUrl }}</a>
        </div>
      </div>
      <ThemeEditor></ThemeEditor>
    </div>
    <h2 v-if="showAllTabsContent" class="text-xl font-semibold leading-7 text-gray-600 border-b border-gray-300 py-2">Embed Code</h2>
    <div v-if="tabs[1].active" class="w-full font-source p-2">
      <Message severity="info" class="w-full" :closable="false"> Place Embed Code below on your website to enable Bot.</Message>
      <Button label="Copy Embed Code" size="small" icon="pi pi-clipboard" severity="primary" @click="copyEmbedCode" autofocus />
      <div class="mt-2" v-html="embedCodeHtml"></div>
    </div>
    <div class="my-2 border-t py-4">
      <Button label="Clear Bot Conversation" size="small" icon="pi pi-times" severity="primary" @click="clearBotConversation" />
    </div>
    <enegelai-bot class="" v-if="botAvailable" ref="botWidget" :name="botTitle" :url="botServiceURL" :org-id="botOrgId" :bot-id="botsStore.botId" :logo-url="botLogoUrl"></enegelai-bot>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted, nextTick } from 'vue';
  import { useAuthStore } from '@/store/AuthStore';
  import { useBotsStore } from '@/store/BotsStore';
  import { useApi } from '@/hooks/useApi';
  import { useMd } from '@/hooks/useMd';
  import ThemeEditor from '@/components/Bots/appearance/ThemeEditor.vue';
  import useBotServiceURL from '@/hooks/useBotServiceURL';
  import generateBotEmbedCode from '@/components/Bots/appearance/BotEmbedCodeGenerator';
  import generateBotTestPage from '@/components/Bots/appearance/BotTestPageGenerator';
  import TabsResponsive from '@/components/Tabs/TabsResponsive.vue';
  import Message from 'primevue/message';
  import Button from 'primevue/button';
  import { useToast } from 'vue-toastification';

  const botWidget = ref(null);

  const authStore = useAuthStore();
  const botsStore = useBotsStore();
  const api = useApi();
  const { botServiceURL } = useBotServiceURL();
  const md = useMd();
  const toast = useToast();

  const themeCss = ref('');
  const themeEmbedCode = ref('');

  const botTitle = ref('');
  const botLogoUrl = ref('');

  // console.log(botServiceURL2.botServiceURL);
  const botOrgId = computed(() => authStore.tenantId);

  const botAvailable = ref(false);
  const botStyleElement = ref(null);

  const tabs = ref([
    { name: 'Appearance', href: '#', icon: 'palette', active: true, hidden: false },
    { name: 'Embed Code', href: '#', icon: 'code-block-tags', active: false, hidden: false },
  ]);
  const tabsHidden = computed(() => [false, false]);
  const showAllTabsContent = ref(false);

  const embedCodeHtml = computed(() => {
    return md.mdToHtml('```html\n' + themeEmbedCode.value + '\n```\n');
  });

  watch(
    () => botsStore.botId,
    async () => {
      await initBot();
    },
  );

  watch(
    () => botsStore.botTheme,
    async (themeData) => {
      //console.log(`Bot theme changed: ${JSON.stringify(themeData)}`);
      await initTheme(themeData);
    },
    { deep: true },
  );

  function copyEmbedCode() {
    navigator.clipboard.writeText(themeEmbedCode.value);
  }

  function clearBotConversation() {
    const chatBot = botWidget.value;
    if (chatBot) {
      chatBot.clearConversation();
    }
  }

  function clearBotLocalStorage() {
    // clear all local storage
    localStorage.removeItem('cb__actions');
    localStorage.removeItem('cb__connectWs');
    localStorage.removeItem('cb__conversationId');
    localStorage.removeItem('cb__feedbackEmail');
    localStorage.removeItem('cb__feedbackName');
    localStorage.removeItem('cb__language');
    localStorage.removeItem('cb__lastConversationUpdate');
    localStorage.removeItem('cb__messages');
    localStorage.removeItem('cb__open');
    localStorage.removeItem('cb__setting');
    localStorage.removeItem('cb__shouldEngage');
    localStorage.removeItem('cb__theme');
  }

  async function initBot() {
    if (botsStore.botId) {
      botAvailable.value = true;
      await nextTick();
      const storedBotId = localStorage.getItem('c7o.bot.test.botId');
      console.log(`Got c7o.bot.test.botId = ${storedBotId}`);
      if (!storedBotId) {
        localStorage.setItem('c7o.bot.test.botId', botsStore.botId);
      } else if (storedBotId !== botsStore.botId) {
        console.log(`Bot under test changed: cleanup`);
        clearBotConversation();
        clearBotLocalStorage();
        if (botWidget.value) {
          try {
            botWidget.value.store.state.reset(); // ! important
          } catch (e) {
            console.log(`Failed to reset bot state - ${e}`);
          }
        }
        botAvailable.value = false;
        await nextTick();
        // Clear conversation if botId has changed
        localStorage.setItem('c7o.bot.test.botId', botsStore.botId);
        console.log(`Bot under test updated: c7o.bot.test.botId changed to ${botsStore.botId}`);
        botAvailable.value = true;
      }
    }
  }

  async function initTheme(themeData) {
    const css = cssFromBotTheme(themeData);
    themeCss.value = css;
    if (botStyleElement.value) {
      botStyleElement.value.textContent = css;
    }
    const embedCode = embedCodeFromBotTheme(themeData, css);
    themeEmbedCode.value = embedCode;
    botTitle.value = themeData?.title || '';
    const oldLogoUrl = botLogoUrl.value;
    const newLogoUrl = themeData?.logoUrl || '';
    if (oldLogoUrl !== newLogoUrl) {
      console.log(`Logo URL changed: ${oldLogoUrl} -> ${newLogoUrl}`);
      botLogoUrl.value = newLogoUrl;
      // [sv2] Need to change title so logo change will take effect - bot widget issue
      const title = botTitle.value;
      botTitle.value = title + ' ';
      await nextTick();
      botTitle.value = title;
    }
  }

  function SaveBot(showToast = true) {
    // Save the latest value
    botsStore.saveBot().then((saveResult) => {
      if (showToast) {
        if (saveResult) {
          toast.success('Bot saved successfully');
        } else {
          toast.error(`Error saving bot`);
        }
      }
    });
  }

  // Generate CSS from bot theme
  function cssFromBotTheme(botThemeData) {
    if (!botThemeData) {
      return '';
    }
    const componentName = botThemeData?.componentName || 'enegelai-bot';
    const cssVariables = botThemeData?.cssVariables || {};
    let css = '';
    css += `${componentName} {\n`;
    for (const [key, value] of Object.entries(cssVariables)) {
      css += `  ${key}: ${value};\n`;
    }
    css += '}\n';
    return css;
  }

  function embedCodeFromBotTheme(botThemeData, css = null) {
    const embedCode = generateBotEmbedCode({
      tenantId: authStore.tenantId,
      botId: botsStore.botId,
      title: botThemeData?.title || '',
      logoUrl: botThemeData?.logoUrl || '',
      css: css ? css : cssFromBotTheme(botThemeData),
      botServiceURL: botServiceURL.value,
    });
    return embedCode;
  }

  function initStyleElement() {
    const id = 'c7o-bot-style';
    // Check if an element with the given id exists
    let styleElement = document.getElementById(id);

    // If the element exists and it's a <style> element, return it
    if (styleElement && styleElement.tagName.toLowerCase() === 'style') {
      botStyleElement.value = styleElement;
    }

    // Otherwise, create a new <style> element with the provided id
    styleElement = document.createElement('style');
    styleElement.id = id;

    // Append the new style element to the document
    document.head.appendChild(styleElement);

    botStyleElement.value = styleElement;
  }

  function base64Encode(str) {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(str);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  }

  async function PublishTestPage() {
    const botTestPageHtml = generateBotTestPage({
      title: botTitle.value,
      logo: botLogoUrl.value,
      botEmbedCode: themeEmbedCode.value,
    });

    const base64Data = base64Encode(botTestPageHtml);

    const cdnUrl = await api.publishToCDN({
      contentType: 'text/html',
      data: base64Data,
      path: `bot/${botsStore.botId}/test.html`,
    });

    if (cdnUrl && cdnUrl !== '') {
      botsStore.botTestUrl = cdnUrl;
      toast.success('Bot test page published successfully:\n' + cdnUrl);
      // [sv2] Save bot as well
      SaveBot();
    } else {
      botsStore.botTestUrl = null;
      toast.error('Failed to publish bot test page');
    }
  }

  onMounted(() => {
    initBot();
    initStyleElement();
    initTheme(botsStore.botTheme);
  });
</script>
